import { useCallback, useContext, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

import { SwapMainAppContext } from '../../../../context/SwapMainAppContext';
import { PRISMAX_DATA_PIPELINE_URL } from '../../../../configs/config';
import modalStyles from './Modals.module.css';
import styles from './QAReviewSuccessModal.module.css';
import EarningsModal from './EarningsModal';

const SETTINGS_KEY = 'dont_show_again_qa_review_success_modal';

export default function QAReviewSuccessModal({ episodeId, onContinue, onClose }) {
    const { setUserSettings, gatewayToken } = useContext(SwapMainAppContext);
    const token = gatewayToken || localStorage.getItem('gatewayToken');

    const [dontShowAgain, setDontShowAgain] = useState(false);

    const [showEarningsModal, setShowEarningsModal] = useState(false);

    // useEffect(() => {
    //     const onKeyDown = (e) => {
    //         if (e.key === 'Escape') handleClose();
    //     };
    //     document.addEventListener('keydown', onKeyDown);
    //     return () => document.removeEventListener('keydown', onKeyDown);
    // }, [handleClose]);

    const handleOverlayClick = async (e) => {
        if (e.target === e.currentTarget) handleContinue();
    };

    const handleUpdateDontShowAgain = async () => {
        if (dontShowAgain) {
            setUserSettings(prev => ({
                ...prev,
                [SETTINGS_KEY]: true
            }));
            try {
                await fetch(`${PRISMAX_DATA_PIPELINE_URL}/data/user-settings/dont-show-again-qa-review-success-modal`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                });
            } catch (err) {
                console.error('Failed to save QA review success modal preference:', err);
            }
        }
    };

    const handleContinue = async () => {
        await handleUpdateDontShowAgain();
        onClose();
        await onContinue();
    };

    const modal = (
        <div className={modalStyles.overlay} onClick={handleOverlayClick}>
            <div className={`${modalStyles.shell} ${styles.shell}`} role="dialog" aria-modal="true" aria-labelledby="qars-title">
                {/* Header */}
                <div className={modalStyles.head}>
                    <div className={modalStyles.headInner}>
                        <div className={modalStyles.headLeft}>
                            <span className={modalStyles.eyebrow}>Episode #{episodeId}</span>
                            <span className={modalStyles.titleTxt} id="qars-title">Results recorded</span>
                        </div>
                    </div>
                    <button className={modalStyles.closeBtn} onClick={handleContinue} aria-label="Close">
                        &times;
                    </button>
                </div>

                {/* Body */}
                <div className={styles.body}>
                    {/* Success card */}
                    <div className={styles.successCard}>
                        <div className={styles.successIconWrap}>
                            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                                <path d="M3.5 9l4 4 7-8" stroke="#60c87a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        <div className={styles.successText}>
                            <div className={styles.successTitle}>Your scores have been submitted</div>
                            <div className={styles.successDesc}>Points will be granted once a consensus score is reached across all validators.</div>
                        </div>
                    </div>

                    {/* Points explanation card */}
                    <div className={styles.pointsCard}>
                        <div className={styles.pointsCardLabel}>How points are awarded</div>
                        <div className={styles.stepRow}>
                            <div className={styles.stepNum}>1</div>
                            <div className={styles.stepText}>Multiple validators score the same episode independently</div>
                        </div>
                        <div className={styles.stepRow}>
                            <div className={styles.stepNum}>2</div>
                            <div className={styles.stepText}>A consensus score is calculated from all submissions</div>
                        </div>
                        <div className={styles.stepRow}>
                            <div className={styles.stepNum}>3</div>
                            <div className={styles.stepText}>
                                If your score is within <strong className={styles.highlightWhite}>&plusmn;15%</strong> of the consensus and your pass/fail checks match, you earn <strong className={styles.highlightBlue}>100 points</strong>
                            </div>
                        </div>
                    </div>

                    {/* Action buttons */}
                    <div className={styles.btnRow}>
                        <button
                            className={`${modalStyles.mbtn} ${modalStyles.mbtnCta} ${styles.btnFlex}`}
                            onClick={handleContinue}
                        >
                            Continue reviewing
                        </button>
                        <button
                            className={`${modalStyles.mbtn} ${modalStyles.mbtnGhost} ${styles.btnFlex}`}
                            onClick={() => setShowEarningsModal(true)}
                        >
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.1" />
                                <path d="M6 3.5v.8M6 7.2v.8M4.5 5.2c0-.6.7-1 1.5-1s1.5.4 1.5 1-.7 1-1.5 1-1.5.4-1.5 1 .7 1 1.5 1" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
                            </svg>
                            My earnings
                        </button>
                    </div>

                    {/* Don't show again */}
                    <div className={styles.dontShowRow}>
                        <label className={styles.dontShowLabel}>
                            <div
                                className={`${styles.dontShowBox} ${dontShowAgain ? styles.dontShowBoxChecked : ''}`}
                                onClick={() => setDontShowAgain(prev => !prev)}
                                // onClick={handleToggleDontShowAgain}
                            >
                                {dontShowAgain && (
                                    <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
                                        <path d="M1 3.5l2.5 2.5L8 1" stroke="#fff" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                )}
                            </div>
                            <span className={styles.dontShowLbl}>Don&apos;t show this again &mdash; skip straight to the next episode</span>
                        </label>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <>
            {ReactDOM.createPortal(modal, document.body)}
            {showEarningsModal && (
                <EarningsModal 
                    onClose={() => setShowEarningsModal(false)}
                    showBackButton
                />
            )}
        </>
    );
}
